import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { AlertCircle, AlertTriangle, Clock } from "lucide-react";

import { Loading } from "@pages/v2/components/layout/Loading";
import { Table } from "@pages/v2/components/ui/table/Table";
import { formatAmount } from "@pages/v2/queries/transactions/format";
import { fetchNeedsAttention } from "@pages/v2/queries/transactions/transactionQueries";
import type {
  NeedsAttentionResponse,
  StuckTransaction,
  TransactionSummary,
} from "@pages/v2/queries/transactions/types";

// "100h" → "4d 4h"; under a day stays as "17h"; zero-safe for edge cases.
const formatAge = (hours: number): string => {
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  const remaining = hours % 24;
  return remaining === 0 ? `${days}d` : `${days}d ${remaining}h`;
};

const NeedsAttention = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<NeedsAttentionResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    fetchNeedsAttention()
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm">
        Failed to load transactions: {error}
      </div>
    );
  }
  if (!data) return null;

  const abandoned = data.abandoned ?? [];

  const totalCount =
    abandoned.length +
    data.stuck.length +
    data.voidFailed.length +
    data.settlementFailed.length;

  if (totalCount === 0) {
    return (
      <div className="rounded-md bg-green-50 p-8 text-center text-green-700">
        <p className="font-medium">No transactions need attention.</p>
        <p className="text-sm text-green-600 mt-1">
          Settlement is running cleanly.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {abandoned.length > 0 && (
        <Section
          title="Abandoned"
          description="Settlement retries exhausted — cron has given up. Check Braintree directly; the transaction is likely settlement_declined or voided out-of-band. Use the admin Void button only if Braintree confirms the row is voidable."
          icon={<AlertCircle className="text-red-500" size={18} />}
          count={abandoned.length}
          truncated={data.hasMore?.abandoned ?? false}
        >
          <SummaryTable
            rows={abandoned}
            onRowClick={(id) => navigate(`/transactions/${id}`)}
          />
        </Section>
      )}

      {data.stuck.length > 0 && (
        <Section
          title="Stuck"
          description="Transactions still in `authorized` past the settlement window + grace. Likely a repeated Braintree failure, cron downtime, or DB/Braintree divergence."
          icon={<AlertCircle className="text-red-500" size={18} />}
          count={data.stuck.length}
          truncated={data.hasMore?.stuck ?? false}
        >
          <StuckTable
            rows={data.stuck}
            onRowClick={(id) => navigate(`/transactions/${id}`)}
          />
        </Section>
      )}

      {data.voidFailed.length > 0 && (
        <Section
          title="Void failed"
          description="An admin tried to cancel the charge and Braintree rejected. Needs manual investigation (often: already settled, permission issue)."
          icon={<AlertTriangle className="text-orange-500" size={18} />}
          count={data.voidFailed.length}
          truncated={data.hasMore?.voidFailed ?? false}
        >
          <SummaryTable
            rows={data.voidFailed}
            onRowClick={(id) => navigate(`/transactions/${id}`)}
          />
        </Section>
      )}

      {data.settlementFailed.length > 0 && (
        <Section
          title="Recent settlement failures"
          description="Settle attempted within the last 24h and rejected. Still authorized — cron will keep retrying, but may indicate a systemic issue."
          icon={<Clock className="text-yellow-500" size={18} />}
          count={data.settlementFailed.length}
          truncated={data.hasMore?.settlementFailed ?? false}
        >
          <SummaryTable
            rows={data.settlementFailed}
            onRowClick={(id) => navigate(`/transactions/${id}`)}
          />
        </Section>
      )}
    </div>
  );
};

type SectionProps = {
  title: string;
  description: string;
  icon: ReactNode;
  count: number;
  /** When true, the BE truncated this bucket and there are additional rows
   *  not shown. Drives the "100+" badge and the truncation footer. */
  truncated?: boolean;
  children: ReactNode;
};

const Section = ({
  title,
  description,
  icon,
  count,
  truncated = false,
  children,
}: SectionProps) => (
  <section className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-4">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="text-lg font-semibold">{title}</h3>
      <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
        {count}
        {truncated && "+"}
      </span>
    </div>
    <p className="text-sm text-gray-600">{description}</p>
    <div className="mt-2">{children}</div>
    {truncated && (
      <p className="mt-1 text-xs italic text-gray-500">
        Showing the first {count} rows. Additional rows exist — query the
        database directly to see the full list.
      </p>
    )}
  </section>
);

const StuckTable = ({
  rows,
  onRowClick,
}: {
  rows: StuckTransaction[];
  onRowClick: (id: string) => void;
}) => (
  <Table>
    <thead>
      <tr>
        <th>Transaction ID</th>
        <th>Email</th>
        <th>Amount</th>
        <th>Authorized</th>
        <th>Age</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr
          key={r.transactionId}
          className="cursor-pointer"
          onClick={() => onRowClick(r.transactionId)}
        >
          <td>{r.transactionId}</td>
          <td>{r.email}</td>
          <td>{formatAmount(r.amount, r.currency)}</td>
          <td>
            {formatDistanceToNow(new Date(r.authorizedAt), { addSuffix: true })}
          </td>
          <td>{formatAge(r.hoursSinceAuthorized)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const SummaryTable = ({
  rows,
  onRowClick,
}: {
  rows: TransactionSummary[];
  onRowClick: (id: string) => void;
}) => (
  <Table>
    <thead>
      <tr>
        <th>Transaction ID</th>
        <th>Email</th>
        <th>Amount</th>
        <th>Authorized</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr
          key={r.transactionId}
          className="cursor-pointer"
          onClick={() => onRowClick(r.transactionId)}
        >
          <td>{r.transactionId}</td>
          <td>{r.email}</td>
          <td>{formatAmount(r.amount, r.currency)}</td>
          <td>
            {formatDistanceToNow(new Date(r.authorizedAt), { addSuffix: true })}
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

export default NeedsAttention;
